// Состав пояснительной записки
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  CFormLabel,
  CFormInput,
  CFormTextarea,
  CForm,
  CContainer,
  CRow,
  CCol,
  CFormCheck,
  CButton,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell
} from '@coreui/react';
import PropTypes from 'prop-types';

const ExplanatoryNoteTab = forwardRef(({ initialData, onSave }, ref) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  useEffect(() => {
    if (initialData && initialData.documents) {
      setDocuments(initialData.documents);
    }
  }, [initialData]);

  const defaultDocuments = [
    { id: 1, number: 1, name: 'Содержание' },
    { id: 2, number: 2, name: 'Введение' },
    { id: 3, number: 3, name: 'Программа проведения обследования строительных конструкций' },
    { id: 4, number: 4, name: 'Перечень технических специалистов' },
    { id: 5, number: 5, name: 'Средства измерения и контроля' },
    { id: 6, number: 6, name: 'Климатические параметры района установки' },
    { id: 7, number: 7, name: 'Анализ проектной, исполнительной и эксплуатационной документации - Форма 1' },
    { id: 8, number: 8, name: 'Конструктивные решения' },
    { id: 9, number: 9, name: 'Технологическое оборудование' },
    { id: 10, number: 10, name: 'Критерии оценки технического состояния конструкций' },
    { id: 11, number: 11, name: 'Оценка технического состояния конструкций и анализ результатов обследования' },
    { id: 12, number: 12, name: 'Рекомендации по дальнейшей эксплуатации объекта - Форма 2' }
  ];

  const handleDocumentClick = (id) => {
    setSelectedDocumentId(id);
  };

  const handleAddDocument = () => {
    const newId = Math.max(...documents.map(item => item.id), 0) + 1;
    const newNumber = documents.length + 1;
    
    const newDocument = {
      id: newId,
      number: newNumber,
      name: ''
    };
    
    const updatedDocuments = [...documents, newDocument];
    setDocuments(updatedDocuments);
    setSelectedDocumentId(newId);
  };

  const handleRemoveDocument = () => {
    if (selectedDocumentId && documents.length > 0) {
      const confirmDelete = window.confirm('Удалить выбранный документ?');
      
      if (confirmDelete) {
        const updatedDocuments = documents.filter(item => item.id !== selectedDocumentId);
        
        // Перенумеровываем оставшиеся документы
        const renumberedDocuments = updatedDocuments.map((item, index) => ({
          ...item,
          number: index + 1
        }));
        
        setDocuments(renumberedDocuments);
        setSelectedDocumentId(renumberedDocuments.length > 0 ? renumberedDocuments[0].id : null);
      }
    } else if (documents.length > 0) {
      const confirmDelete = window.confirm('Удалить последний документ?');
      
      if (confirmDelete) {
        const updatedDocuments = documents.slice(0, -1);
        
        // Перенумеровываем оставшиеся документы
        const renumberedDocuments = updatedDocuments.map((item, index) => ({
          ...item,
          number: index + 1
        }));
        
        setDocuments(renumberedDocuments);
        setSelectedDocumentId(renumberedDocuments.length > 0 ? renumberedDocuments[renumberedDocuments.length - 1].id : null);
      }
    }
  };

  const handleMoveUp = () => {
    if (selectedDocumentId && documents.length > 1) {
      const index = documents.findIndex(item => item.id === selectedDocumentId);
      if (index > 0) {
        const updatedDocuments = [...documents];
        [updatedDocuments[index - 1], updatedDocuments[index]] = [updatedDocuments[index], updatedDocuments[index - 1]];
        
        // Перенумеровываем
        const renumberedDocuments = updatedDocuments.map((item, idx) => ({
          ...item,
          number: idx + 1
        }));
        
        setDocuments(renumberedDocuments);
      }
    }
  };

  const handleMoveDown = () => {
    if (selectedDocumentId && documents.length > 1) {
      const index = documents.findIndex(item => item.id === selectedDocumentId);
      if (index < documents.length - 1) {
        const updatedDocuments = [...documents];
        [updatedDocuments[index], updatedDocuments[index + 1]] = [updatedDocuments[index + 1], updatedDocuments[index]];
        
        // Перенумеровываем
        const renumberedDocuments = updatedDocuments.map((item, idx) => ({
          ...item,
          number: idx + 1
        }));
        
        setDocuments(renumberedDocuments);
      }
    }
  };

  const handleSetDefault = () => {
    setDocuments([...defaultDocuments]);
    setSelectedDocumentId(defaultDocuments.length > 0 ? defaultDocuments[0].id : null);
  };

  const handleDocumentNameChange = (id, value) => {
    setDocuments(documents.map(item => 
      item.id === id ? { ...item, name: value } : item
    ));
  };

  const getFormDataForSave = () => {
    return {
      documents
    };
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (onSave) {
        const formData = getFormDataForSave();
        await onSave(formData);
      }
    },
    getFormData: getFormDataForSave
  }));

  return (
    <CContainer fluid className="explanatory-note-tab-container">
      <CForm>
        {/* Кнопки управления */}
        <CRow className="mb-3">
          <CCol>
            <div className="d-flex gap-2">
              <CButton color="light" variant="outline" size="sm" className="text-dark border" onClick={handleAddDocument}>
                Добавить
              </CButton>
              <CButton color="light" variant="outline" size="sm" className="text-dark border" onClick={handleRemoveDocument}>
                Удалить
              </CButton>
              <CButton color="light" variant="outline" size="sm" className="text-dark border" onClick={handleMoveUp}>
                Вверх
              </CButton>
              <CButton color="light" variant="outline" size="sm" className="text-dark border" onClick={handleMoveDown}>
                Вниз
              </CButton>
              <CButton color="light" variant="outline" size="sm" className="text-dark border" onClick={handleSetDefault}>
                По умолчанию
              </CButton>
            </div>
          </CCol>
        </CRow>

        {/* Таблица документов */}
        <CRow className="mb-3">
          <CCol>
            <CTable bordered small className="mb-0">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell className="border text-center" style={{ width: '100px' }}>№ раздела</CTableHeaderCell>
                  <CTableHeaderCell className="border text-center">Наименование документа</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {documents.map((item) => (
                  <CTableRow 
                    key={item.id} 
                    onClick={() => handleDocumentClick(item.id)}
                    className={selectedDocumentId === item.id ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    <CTableDataCell className="border text-center" style={{ width: '100px', verticalAlign: 'middle' }}>
                      {item.number}
                    </CTableDataCell>
                    <CTableDataCell className="border p-0">
                      <CFormInput
                        type="text"
                        value={item.name}
                        onChange={(e) => handleDocumentNameChange(item.id, e.target.value)}
                        placeholder="Введите наименование документа"
                        className="border-0 w-100"
                        style={{ borderRadius: '0' }}
                      />
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {documents.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan="2" className="text-center border py-3 text-muted">
                      Нет данных. Нажмите "Добавить" или "По умолчанию" для заполнения таблицы.
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </CCol>
        </CRow>
      </CForm>
    </CContainer>
  );
});

ExplanatoryNoteTab.propTypes = {
  initialData: PropTypes.shape({
    documents: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        number: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired
      })
    )
  }),
  onSave: PropTypes.func
};

ExplanatoryNoteTab.defaultProps = {
  initialData: null,
  onSave: null
};

export default ExplanatoryNoteTab;